import { useState, type ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useSubmitSalesRequestMutation } from '../services/helpSupportApi';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import type {
  EnterpriseFormData,
  EnterpriseFormErrors,
} from '../../../types/help-support.types';

interface EnterpriseAccountModalProps {
  onClose: () => void;
}

const isValidUSPhone = (phone: string) =>
  /^\(?([0-9]{3})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/.test(phone);

const isValidEmail = (email: string) =>
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);

const EnterpriseAccountModal = ({ onClose }: EnterpriseAccountModalProps) => {
  const dispatch = useDispatch();
  const [submitSales, { isLoading }] = useSubmitSalesRequestMutation();
  const [formData, setFormData] = useState<EnterpriseFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    doorsUnderManagement: '',
  });
  const [formErrors, setFormErrors] = useState<EnterpriseFormErrors>({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    companyName: false,
    doorsUnderManagement: false,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): boolean => {
    const errors: EnterpriseFormErrors = {
      firstName: !formData.firstName ? 'Please enter first name' : false,
      lastName: !formData.lastName ? 'Please enter last name' : false,
      email: !formData.email
        ? 'Please enter email'
        : !isValidEmail(formData.email)
          ? 'Please enter a valid email address'
          : false,
      phoneNumber: !formData.phoneNumber
        ? 'Please enter phone number'
        : !isValidUSPhone(formData.phoneNumber)
          ? 'Please enter a valid phone number'
          : false,
      companyName: !formData.companyName ? 'Please enter company name' : false,
      doorsUnderManagement: !formData.doorsUnderManagement
        ? 'Please enter doors under management'
        : false,
    };
    setFormErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await submitSales({
        action: 'Sales Submission',
        content: '',
        ...formData,
      }).unwrap();
      onClose();
      dispatch(
        showAlert({ type: Variants.SUCCESS, message: 'Request has been successfully sent.' }),
      );
    } catch (error: unknown) {
      onClose();
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    }
  };

  const fields: Array<{
    name: keyof EnterpriseFormData;
    label: string;
    placeholder: string;
    type?: string;
  }> = [
    { name: 'firstName', label: 'First Name', placeholder: 'Enter first name' },
    { name: 'lastName', label: 'Last Name', placeholder: 'Enter last name' },
    { name: 'email', label: 'Email', placeholder: 'Enter email', type: 'email' },
    { name: 'phoneNumber', label: 'Phone No.', placeholder: 'Enter phone no.' },
    { name: 'companyName', label: 'Company Name', placeholder: 'Enter company name' },
    { name: 'doorsUnderManagement', label: 'Doors Under Management', placeholder: 'Enter doors under management' },
  ];

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Setup Enterprise Account"
      description="Fill out the form below and our sales team will get back to you."
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoading}
            onClick={handleSubmit}
            className="w-full sm:w-auto"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </>
      }
      size="lg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {fields.map(({ name, label, placeholder, type }) => (
          <div key={name}>
            <Label htmlFor={name} required>
              {label}
            </Label>
            <Input
              id={name}
              name={name}
              type={type ?? 'text'}
              placeholder={placeholder}
              onChange={handleChange}
              className={formErrors[name] ? 'border-destructive' : ''}
              data-testid={`${name}_id`}
            />
            <FormError message={formErrors[name] || undefined} />
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default EnterpriseAccountModal;
